<template>
    <div>
      <Header />
  
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <h5 class="page-title">Manage Ledgers</h5>
          <p class="page-sub">View and manage chart of account ledger entries</p>
        </div>
        <div class="page-actions">
  
          <!-- Search -->
          <div class="search-wrap">
            <font-awesome-icon icon="search" class="search-icon" />
            <input type="text" class="search-input" placeholder="Search ledgers..." @input="tableData.page = 1; fetchData()" v-model="tableData.search" />
          </div>
  
          <!-- Type Filter -->
          <select v-model="tableData.type" @change="tableData.page = 1; fetchData()" class="filter-select">
            <option :value="['asset']">Asset</option>
            <option :value="['liability']">Liability</option>
            <option :value="['equity']">Equity</option>
            <option :value="['income']">Income</option>
            <option :value="['expense']">Expense</option>
            <option :value="allGroups">All Types</option>
          </select>
  
          <!-- Status Filter -->
          <select v-model="tableData.status" @change="tableData.page = 1; fetchData()" class="filter-select">
            <option :value="[1]">Active</option>
            <option :value="[0]">Inactive</option>
            <option :value="[0,1]">All Statuses</option>
          </select>
  
          <!-- Bulk Upload -->
          <button v-if="permission.create_ledger == 1" class="btn-outline" @click="openUploadModal">
            <font-awesome-icon icon="upload" /> Upload
          </button>

          <!-- Create -->
          <button v-if="permission.create_ledger == 1" class="btn-primary-action" @click="openCreateModal">
            <font-awesome-icon icon="plus" /> Add Ledger
          </button>

          <!-- Export -->
          <div class="dropdown">
            <button class="btn-outline dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
              <font-awesome-icon icon="download" /> Export
            </button>
            <ul class="dropdown-menu dropdown-menu-end">
              <li><a class="dropdown-item" href="#" @click="drawPdf(print_content, headerArrayColumn, 'p', 'ledgers', 'Ledgers')">
                <font-awesome-icon icon="file-pdf" class="me-2 text-danger" /> PDF
              </a></li>
              <li><a class="dropdown-item" href="#" @click="reusableMixinToCSV('ledgers.csv')">
                <font-awesome-icon icon="file-csv" class="me-2 text-success" /> CSV
              </a></li>
            </ul>
          </div>
  
        </div>
      </div>
  
      <!-- Data Card -->
      <div class="data-card">
  
        <!-- Loading -->
        <div v-if="loading" class="state-block">
          <div class="spinner-border text-primary" style="width:3rem;height:3rem;" role="status"></div>
          <p class="mt-3 text-muted-cell">Loading ledgers...</p>
        </div>
  
        <!-- 403 -->
        <div v-else-if="permission_status == 403" class="state-block">
          <div class="state-icon state-icon--red">
            <font-awesome-icon icon="lock" />
          </div>
          <p class="state-title">Access Denied</p>
          <p class="state-sub">You don't have permission to view this resource.</p>
        </div>
  
        <!-- Empty -->
        <div v-else-if="details.length === 0" class="state-block">
          <div class="state-icon state-icon--blue">
            <font-awesome-icon icon="layer-group" />
          </div>
          <p class="state-title">No ledgers found</p>
          <p class="state-sub">No ledger accounts match your current filters.</p>
        </div>
  
        <!-- Table -->
        <template v-else>
  
          <div class="table-toolbar">
            <span class="table-count">
              Showing <strong>{{ pagination.from }}–{{ pagination.to }}</strong> of <strong>{{ pagination.total }}</strong>
            </span>
            <div class="toolbar-right">
              <label class="table-count">Rows:</label>
              <select v-model.number="tableData.length" @change="tableData.page = 1; fetchData()" class="rows-select">
                <option v-for="n in [5,10,20,50,100,200,500,1000]" :key="n" :value="n">{{ n }}</option>
              </select>
            </div>
          </div>
  
          <div class="table-wrap">
            <table class="data-table">
              <thead>
                <tr>
                  <th>Ledger Name</th>
                  <th>Code</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th class="col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, index) in details" :key="row.id">
  
                  <td class="fw-bold">{{ row.name }}</td>
  
                  <td>
                    <span class="batch-code">{{ row.code }}</span>
                  </td>
  
                  <td>
                    <span class="type-badge" :class="typeClass(row.type)">{{ capitalize(row.type) }}</span>
                  </td>
  
                  <td>
                    <div class="toggle-wrap">
                      <label class="toggle">
                        <input
                          type="checkbox"
                          :true-value="1" :false-value="0"
                          v-model="statuses[index]"
                          @change="permission.update_ledger_group == 1 && row.can_edit == 1 ? updateLedgerStatus(row.id) : ''"
                          :disabled="permission.update_ledger_group != 1 || row.can_edit != 1"
                        />
                        <span class="toggle-slider"></span>
                      </label>
                      <span class="toggle-label" :class="statuses[index] == 1 ? 'toggle-label--on' : 'toggle-label--off'">
                        {{ statuses[index] == 1 ? 'Active' : 'Inactive' }}
                      </span>
                    </div>
                  </td>
  
                  <td class="col-actions">
                    <div class="action-btns">
                      <button
                        class="action-btn action-btn--edit"
                        :class="(permission.update_ledger != 1 || row.can_edit != 1) ? 'action-btn--disabled' : ''"
                        :disabled="permission.update_ledger != 1 || row.can_edit != 1"
                        @click="permission.update_ledger == 1 && row.can_edit == 1 ? openUpdateModal(index) : ''"
                        title="Edit ledger"
                      >
                        <font-awesome-icon icon="pen" />
                      </button>
                      <button
                        class="action-btn action-btn--delete"
                        :class="(permission.delete_ledger != 1 || row.can_delete != 1) ? 'action-btn--disabled' : ''"
                        :disabled="permission.delete_ledger != 1 || row.can_delete != 1"
                        @click="permission.delete_ledger == 1 && row.can_delete == 1 ? openDeleteModal(index) : ''"
                        title="Delete ledger"
                      >
                        <font-awesome-icon icon="trash" />
                      </button>
                    </div>
                  </td>
  
                </tr>
              </tbody>
            </table>
          </div>
  
          <div v-if="pagination.lastPage > 1" class="pagination-bar">
            <span class="pagination-info">Showing {{ pagination.from }}–{{ pagination.to }} of {{ pagination.total }}</span>
            <div class="pagination-controls">
              <button class="pg-btn" :disabled="pagination.currentPage <= 1" @click="tableData.page = 1; fetchData()">«</button>
              <button class="pg-btn" :disabled="pagination.currentPage <= 1" @click="tableData.page = pagination.currentPage - 1; fetchData()">‹ Prev</button>
              <span class="pg-pages">
                <button v-for="p in pageRange" :key="p" class="pg-num" :class="{ 'pg-num--active': p === pagination.currentPage }" @click="tableData.page = p; fetchData()">{{ p }}</button>
              </span>
              <button class="pg-btn" :disabled="pagination.currentPage >= pagination.lastPage" @click="tableData.page = pagination.currentPage + 1; fetchData()">Next ›</button>
              <button class="pg-btn" :disabled="pagination.currentPage >= pagination.lastPage" @click="tableData.page = pagination.lastPage; fetchData()">»</button>
            </div>
          </div>
  
        </template>
      </div>
  
  
      <!-- ── Bulk Upload Modal ── -->
      <div class="modal fade" id="upload_modal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered">
          <div class="modal-content modal-clean">
            <div class="modal-header-clean">
              <div class="modal-title-icon" style="background:rgba(0,74,173,.1);color:#004AAD;">
                <font-awesome-icon icon="upload" />
              </div>
              <div>
                <h6 class="modal-clean-title">Bulk Upload Ledgers</h6>
                <p class="modal-clean-sub">Import multiple ledger accounts from a spreadsheet</p>
              </div>
              <button type="button" class="btn-close ms-auto" @click="closeUploadModal" aria-label="Close"></button>
            </div>
            <div class="modal-body" style="display:flex;flex-direction:column;gap:16px;">
              <!-- Template download -->
              <div class="upload-template-row">
                <font-awesome-icon icon="file-excel" class="upload-template-icon" />
                <div>
                  <p class="upload-template-title">Download Template</p>
                  <p class="upload-template-sub">Use the official template so column headers match exactly.</p>
                </div>
                <button class="btn-template-dl" @click="downloadLedgerTemplate">
                  <font-awesome-icon icon="download" /> Download
                </button>
              </div>

              <!-- Template columns hint -->
              <div class="upload-cols-hint">
                <span class="col-chip">name</span>
                <span class="col-chip">type</span>
                <span class="col-chip col-chip--optional">parent_name <em>(optional)</em></span>
              </div>
              <p class="upload-types-note">Valid types: <code>asset</code> · <code>liability</code> · <code>equity</code> · <code>income</code> · <code>expense</code></p>

              <!-- File picker -->
              <div class="field-group">
                <label class="field-label">Select File <span class="req">*</span></label>
                <input type="file" class="field-input upload-file-input" accept=".xlsx,.xls,.csv"
                  ref="ledgerFileInput" @change="onLedgerFileChange" />
              </div>

              <!-- Result after upload -->
              <div v-if="uploadResult" class="upload-result-box" :class="uploadResult.errors && uploadResult.errors.length ? 'upload-result-box--warn' : 'upload-result-box--success'">
                <p class="upload-result-title">
                  <font-awesome-icon :icon="uploadResult.errors && uploadResult.errors.length ? 'exclamation-triangle' : 'check-circle'" class="me-2" />
                  {{ uploadResult.message }}
                </p>
                <ul v-if="uploadResult.errors && uploadResult.errors.length" class="upload-error-list">
                  <li v-for="(err, i) in uploadResult.errors" :key="i">{{ err }}</li>
                </ul>
              </div>
            </div>
            <div class="modal-footer border-0 pt-0">
              <button type="button" @click="closeUploadModal" class="btn-outline">Close</button>
              <button class="btn-primary-action" @click="submitLedgerUpload" :disabled="!uploadFile || uploadLoading">
                <span v-if="uploadLoading" class="spinner-border spinner-border-sm me-2" role="status"></span>
                {{ uploadLoading ? 'Uploading...' : 'Upload Ledgers' }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- ── Create Modal ── -->
      <div class="modal fade" id="create_modal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered">
          <div class="modal-content modal-clean">
            <div class="modal-header-clean">
              <div class="modal-title-icon" style="background:rgba(0,74,173,.1);color:#004AAD;">
                <font-awesome-icon icon="plus" />
              </div>
              <div>
                <h6 class="modal-clean-title">New Ledger Account</h6>
                <p class="modal-clean-sub">Add a custom account to the chart of accounts</p>
              </div>
              <button type="button" class="btn-close ms-auto" @click="closeCreateModal" aria-label="Close"></button>
            </div>
            <form @submit.prevent="createLedger">
              <div class="modal-body" style="display:flex;flex-direction:column;gap:14px;">
                <div class="field-group">
                  <label class="field-label">Account Name <span class="req">*</span></label>
                  <input type="text" class="field-input" placeholder="e.g. Office Equipment" v-model="createForm.name" />
                </div>
                <div class="field-group">
                  <label class="field-label">Account Type <span class="req">*</span></label>
                  <select class="field-input" v-model="createForm.type" @change="createForm.parent_id = ''">
                    <option value="">— Select type —</option>
                    <option value="asset">Asset</option>
                    <option value="liability">Liability</option>
                    <option value="equity">Equity</option>
                    <option value="income">Income</option>
                    <option value="expense">Expense</option>
                  </select>
                </div>
                <div class="field-group" v-if="createForm.type">
                  <label class="field-label">Parent Account <span class="field-hint">(optional — leave blank for top-level)</span></label>
                  <select class="field-input" v-model="createForm.parent_id">
                    <option value="">— None (top-level) —</option>
                    <option v-for="acc in parentAccounts" :key="acc.id" :value="acc.id">
                      {{ acc.code }} — {{ acc.name }}
                    </option>
                  </select>
                </div>
                <p class="code-hint">
                  <font-awesome-icon icon="info-circle" class="me-1" />
                  Account code is assigned automatically based on type and position.
                </p>
              </div>
              <div class="modal-footer border-0 pt-0">
                <button type="button" @click="closeCreateModal" class="btn-outline">Cancel</button>
                <button type="submit" class="btn-primary-action" :disabled="createLoading">
                  <span v-if="createLoading" class="spinner-border spinner-border-sm me-2" role="status"></span>
                  {{ createLoading ? 'Creating...' : 'Create Ledger' }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <!-- ── Update Modal ── -->
      <div class="modal fade" id="update_modal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered">
          <div class="modal-content modal-clean">
            <div class="modal-header-clean">
              <div class="modal-title-icon" style="background:rgba(13,148,136,.1);color:#0d9488;">
                <font-awesome-icon icon="pen" />
              </div>
              <div>
                <h6 class="modal-clean-title">Update Ledger</h6>
                <p class="modal-clean-sub">Edit the ledger name</p>
              </div>
              <button type="button" class="btn-close ms-auto" @click="closeUpdateModal" aria-label="Close"></button>
            </div>
            <form @submit.prevent="updateLedger">
              <div class="modal-body">
                <div class="field-group">
                  <label class="field-label">Ledger Name</label>
                  <input type="text" class="field-input" placeholder="Enter ledger name" v-model="currentData.name" />
                </div>
              </div>
              <div class="modal-footer border-0 pt-0">
                <button type="button" @click="closeUpdateModal" class="btn-outline">Cancel</button>
                <button type="submit" class="btn-primary-action" :disabled="loading">
                  <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status"></span>
                  {{ loading ? 'Saving...' : 'Update Ledger' }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
  
  
      <!-- ── Delete Modal ── -->
      <div class="modal fade" id="delete_modal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered modal-sm">
          <div class="modal-content modal-clean">
            <div class="modal-icon-wrap">
              <div class="modal-icon modal-icon--danger">
                <font-awesome-icon icon="trash" />
              </div>
            </div>
            <div class="modal-body-clean">
              <p class="modal-heading">Delete Ledger?</p>
              <p class="modal-subtext">
                Are you sure you want to delete <strong>{{ currentData.name }}</strong>? This cannot be undone.
              </p>
            </div>
            <div class="modal-actions">
              <button type="button" class="modal-btn modal-btn--cancel" data-bs-dismiss="modal">Cancel</button>
              <button type="button" class="modal-btn modal-btn--confirm modal-btn--danger" data-bs-dismiss="modal" @click.prevent="deleteLedger">
                <font-awesome-icon icon="trash" class="me-2" /> Delete
              </button>
            </div>
          </div>
        </div>
      </div>
  
    </div>
  </template>
  
  <script>
  import { reusableMixin } from '../../../lib/reusableMixin';
  import Header from './component/header.vue';
  import axios from 'axios';
  import { useToast } from 'vue-toastification';
  import { useStore } from 'vuex';
  
  export default {
    components: { Header },
    mixins: [reusableMixin],
  
    data() {
      return {
        print_content: [],
        headerArrayColumn: ['S/N','Ledger Name','Code','Type','Status'],
        details: [], statuses: [], loading: false, currentData: {},
        permission_status: null,
        allGroups: ['asset','liability','equity','income','expense'],
        allAccounts: [],
        createForm: { name: '', type: '', parent_id: '' },
        createLoading: false,
        uploadFile: null,
        uploadLoading: false,
        uploadResult: null,
        tableData: { page: 1,
          draw: 0, length: 5, type: null, search: '', column: 0,
          dir: 'desc', id: '', status: [0,1],
        },
        pagination: { lastPage:'', currentPage:'', total:'', lastPageUrl:'', nextPageUrl:'', prevPageUrl:'', from:'', to:'' },
        toast: useToast(),
        store: useStore(),
      };
    },
  
    computed: {
      permission() { return this.store.state.getCurrentUserPermission; },
      parentAccounts() {
        return this.allAccounts.filter(a => a.type === this.createForm.type && !a.parent_id);
      },
      pageRange() {
        if (!this.pagination.lastPage) return [];
        const cur = this.pagination.currentPage, last = this.pagination.lastPage;
        const range = [];
        for (let i = Math.max(1, cur - 2); i <= Math.min(last, cur + 2); i++) range.push(i);
        return range;
      },
    },
  
    created() { this.tableData.type = this.allGroups; this.fetchData(); this.fetchAllAccounts(); },
  
    methods: {
      dataToPrint(details) {
        this.print_content = details.map((d, idx) => ({
          sn: idx + 1, name: d.name, code: d.code, type: d.type, status: d.is_active ? 'Active' : 'Inactive',
        }));
      },
  
      fetchData(url = '/accounting/ledgers') {
        this.loading = true;
        this.tableData.draw++;
        axios.get(url, { params: this.tableData })
          .then(response => {
            const data   = response.data;
            this.details = data.data.data;
            this.statuses = this.details.map(c => c.is_active);
            this.dataToPrint(this.details);
            this.configPagination(data.data);
            this.loading = false;
          })
          .catch(error => {
            this.loading = false;
            if (error?.response?.status == 403) {
              this.permission_status = 403;
              this.toast.error(error.response.data.message);
            }
          });
      },
  
      async updateLedger() {
        this.loading = true;
        axios.put('/accounting/ledger/update', this.currentData)
          .then(() => {
            this.toast.success('Ledger updated successfully');
            this.fetchData(); this.loading = false; this.closeUpdateModal();
          })
          .catch(error => {
            this.loading = false;
            const status = error.response?.status;
            if (status == 422) this.toast.warning('There are errors in your input');
            else if (status == 400) this.toast.error('This ledger cannot be edited');
            else this.toast.error('Something went wrong');
          });
      },
  
      async updateLedgerStatus(id) {
        axios.put(`accounting/ledger/update-status/${id}`)
          .then(res => { this.toast.success(res.data.message); this.fetchData(); })
          .catch(() => { this.toast.error('Could not update status'); });
      },
  
      async deleteLedger() {
        axios.delete(`/accounting/ledger/${this.currentData.id}`)
          .then(() => { this.toast.success('Ledger deleted successfully'); this.fetchData(); })
          .catch(error => { this.toast.error(error.response?.data?.message ?? 'Could not delete ledger'); });
      },
  
      fetchAllAccounts() {
        axios.get('/accounting/active-ledgers')
          .then(res => { this.allAccounts = res.data.data ?? []; })
          .catch(() => {});
      },

      openUploadModal() {
        this.uploadFile   = null;
        this.uploadResult = null;
        if (this.$refs.ledgerFileInput) this.$refs.ledgerFileInput.value = '';
        window.$('#upload_modal').modal('show');
      },
      closeUploadModal() { window.$('#upload_modal').modal('hide'); },
      onLedgerFileChange(e) { this.uploadFile = e.target.files[0] ?? null; this.uploadResult = null; },

      downloadLedgerTemplate() {
        axios.get('/accounting/ledger/template', { responseType: 'blob' })
          .then(res => {
            const url  = URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url; link.download = 'ledger_upload_template.xlsx';
            link.click(); URL.revokeObjectURL(url);
          })
          .catch(() => this.toast.error('Could not download template'));
      },

      async submitLedgerUpload() {
        if (!this.uploadFile) { this.toast.warning('Please select a file first'); return; }
        this.uploadLoading = true;
        this.uploadResult  = null;
        const form = new FormData();
        form.append('file', this.uploadFile);
        axios.post('/accounting/ledger/import', form, { headers: { 'Content-Type': 'multipart/form-data' } })
          .then(res => {
            const data = res.data;
            this.uploadResult = { message: data.message, errors: data.data?.errors ?? [] };
            if (data.data?.created > 0) { this.fetchData(); this.fetchAllAccounts(); }
          })
          .catch(err => {
            const msg    = err.response?.data?.message ?? 'Upload failed';
            const errors = err.response?.data?.data?.errors ?? [];
            this.uploadResult = { message: msg, errors };
            this.toast.error(msg);
          })
          .finally(() => { this.uploadLoading = false; });
      },

      openCreateModal() {
        this.createForm = { name: '', type: '', parent_id: '' };
        window.$('#create_modal').modal('show');
      },

      closeCreateModal() { window.$('#create_modal').modal('hide'); },

      async createLedger() {
        if (!this.createForm.name.trim()) { this.toast.warning('Account name is required'); return; }
        if (!this.createForm.type)        { this.toast.warning('Account type is required'); return; }
        this.createLoading = true;
        axios.post('/accounting/ledger/create', this.createForm)
          .then(() => {
            this.toast.success('Ledger account created successfully');
            this.closeCreateModal();
            this.fetchData();
            this.fetchAllAccounts();
          })
          .catch(error => {
            const status = error.response?.status;
            if (status === 422) this.toast.warning(error.response.data.message);
            else if (status === 400) this.toast.error(error.response.data.message);
            else this.toast.error('Something went wrong');
          })
          .finally(() => { this.createLoading = false; });
      },

      openUpdateModal(index) { this.currentData = { ...this.details[index] }; window.$('#update_modal').modal('show'); },
      openDeleteModal(index) { this.currentData = this.details[index]; window.$('#delete_modal').modal('show'); },
      closeUpdateModal()     { window.$('#update_modal').modal('hide'); },
      capitalize(text)       { return text ? text.charAt(0).toUpperCase() + text.slice(1) : ''; },
  
      typeClass(type) {
        const map = { asset:'type--asset', liability:'type--liability', equity:'type--equity', income:'type--income', expense:'type--expense' };
        return map[type] ?? '';
      },
  
      configPagination(data) {
        this.pagination.lastPage    = data.last_page;
        this.pagination.currentPage = data.current_page;
        this.pagination.total       = data.total;
        this.pagination.lastPageUrl = data.last_page_url;
        this.pagination.nextPageUrl = data.next_page_url;
        this.pagination.prevPageUrl = data.prev_page_url;
        this.pagination.from        = data.from;
        this.pagination.to          = data.to;
      },
    },
  };
  </script>
  
  <style scoped>
  /* ── Page Header ── */
  .page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:14px; margin-bottom:20px; }
  .page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
  .page-sub     { font-size:13px; color:#7a90b0; margin:0; }
  .page-actions { display:flex; align-items:center; flex-wrap:wrap; gap:8px; }
  
  /* ── Search / Filters ── */
  .search-wrap  { position:relative; display:flex; align-items:center; }
  .search-icon  { position:absolute; left:11px; color:#7a90b0; font-size:13px; pointer-events:none; }
  .search-input { padding:8px 12px 8px 32px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; width:190px; outline:none; transition:border-color .15s; }
  .search-input:focus { border-color:#004AAD; }
  .search-input::placeholder { color:#aab8cc; }
  .filter-select { padding:8px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; outline:none; cursor:pointer; }
  
  /* ── Buttons ── */
  .btn-primary-action { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:8px 16px; border-radius:8px; border:none; text-decoration:none; cursor:pointer; transition:background .15s; white-space:nowrap; }
  .btn-primary-action:hover:not(:disabled) { background:#003b8e; }
  .btn-primary-action:disabled { opacity:.6; cursor:not-allowed; }
  .btn-outline { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#1a2b45; border:1px solid #dde5f0; font-size:13px; font-weight:500; padding:8px 14px; border-radius:8px; cursor:pointer; transition:background .15s; white-space:nowrap; }
  .btn-outline:hover { background:#f4f7fb; }
  .btn-outline::after { display:none !important; }
  
  /* ── Data Card ── */
  .data-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
  
  /* ── States ── */
  .state-block  { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:64px 24px; text-align:center; }
  .state-icon   { width:60px; height:60px; border-radius:16px; display:flex; align-items:center; justify-content:center; font-size:24px; margin-bottom:16px; }
  .state-icon--blue { background:rgba(0,74,173,.1); color:#004AAD; }
  .state-icon--red  { background:rgba(224,53,53,.1); color:#e03535; }
  .state-title  { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
  .state-sub    { font-size:13px; color:#7a90b0; margin:0; }
  .mt-3         { margin-top:12px; }
  
  /* ── Toolbar ── */
  .table-toolbar { display:flex; align-items:center; justify-content:space-between; padding:14px 20px; border-bottom:1px solid #e4eaf2; flex-wrap:wrap; gap:10px; }
  .table-count   { font-size:13px; color:#7a90b0; }
  .toolbar-right { display:flex; align-items:center; gap:8px; }
  .rows-select   { border:1px solid #dde5f0; border-radius:7px; padding:5px 10px; font-size:13px; color:#1a2b45; background:#f8fafc; outline:none; cursor:pointer; }
  
  /* ── Table ── */
  .table-wrap  { overflow-x:auto; }
  .data-table  { width:100%; border-collapse:collapse; }
  .data-table thead tr { background:#f8fafc; }
  .data-table thead th { font-size:11.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; padding:12px 14px; border-bottom:1px solid #e4eaf2; white-space:nowrap; }
  .data-table tbody tr { border-bottom:1px solid #f0f4f8; transition:background .12s; }
  .data-table tbody tr:last-child { border-bottom:none; }
  .data-table tbody tr:hover { background:#f8fafc; }
  .data-table tbody td { padding:12px 14px; font-size:13.5px; vertical-align:middle; }
  .col-actions { width:80px; }
  .fw-bold     { font-weight:700; color:#1a2b45; }
  
  /* ── Batch Code ── */
  .batch-code { font-family:monospace; font-size:12.5px; font-weight:600; color:#1a2b45; background:#f4f7fb; border:1px solid #e4eaf2; border-radius:6px; padding:2px 8px; }
  
  /* ── Type Badges ── */
  .type-badge      { display:inline-block; border-radius:6px; padding:3px 10px; font-size:12px; font-weight:600; border:1px solid transparent; }
  .type--asset     { background:#edf4ff; color:#004AAD; border-color:#c5d6f5; }
  .type--liability { background:#fff0f0; color:#e03535; border-color:#fac5c5; }
  .type--equity    { background:#f5f3ff; color:#7c3aed; border-color:#c4b5fd; }
  .type--income    { background:#f0fdf4; color:#16a34a; border-color:#bbf7d0; }
  .type--expense   { background:#fff7ed; color:#e08c00; border-color:#fed7aa; }
  
  /* ── Toggle Switch ── */
  .toggle-wrap { display:flex; align-items:center; gap:8px; }
  .toggle      { position:relative; display:inline-block; width:36px; height:20px; flex-shrink:0; }
  .toggle input { opacity:0; width:0; height:0; }
  .toggle-slider { position:absolute; cursor:pointer; inset:0; background:#dde5f0; border-radius:20px; transition:background .2s; }
  .toggle-slider::before { content:''; position:absolute; width:14px; height:14px; left:3px; bottom:3px; background:#fff; border-radius:50%; transition:transform .2s; }
  .toggle input:checked + .toggle-slider { background:#004AAD; }
  .toggle input:checked + .toggle-slider::before { transform:translateX(16px); }
  .toggle input:disabled + .toggle-slider { opacity:.45; cursor:not-allowed; }
  .toggle-label     { font-size:12.5px; font-weight:600; }
  .toggle-label--on  { color:#16a34a; }
  .toggle-label--off { color:#7a90b0; }
  
  /* ── Action Buttons ── */
  .action-btns  { display:flex; align-items:center; gap:5px; }
  .action-btn   { width:28px; height:28px; border-radius:7px; border:1px solid #e4eaf2; display:flex; align-items:center; justify-content:center; font-size:11px; cursor:pointer; transition:background .15s; background:#fff; }
  .action-btn--edit   { color:#004AAD; } .action-btn--edit:hover:not([disabled]):not(.action-btn--disabled)   { background:#edf4ff; border-color:#c5d6f5; }
  .action-btn--delete { color:#e03535; } .action-btn--delete:hover:not([disabled]):not(.action-btn--disabled) { background:#fff0f0; border-color:#fac5c5; }
  .action-btn--disabled { color:#dde5f0 !important; cursor:not-allowed; }
  .action-btn:disabled  { color:#dde5f0 !important; cursor:not-allowed; }
  
  /* ── Footer ── */
  .table-footer { padding:14px 20px; border-top:1px solid #e4eaf2; display:flex; justify-content:flex-end; }
  
  /* ── Dropdown ── */
  .dropdown-menu { border:1px solid #e4eaf2; border-radius:9px; box-shadow:0 8px 24px rgba(0,0,0,.08); padding:6px; min-width:170px; }
  .dropdown-item { border-radius:6px; font-size:13px; padding:8px 12px; color:#1a2b45; }
  .dropdown-item:hover { background:#f4f7fb; }
  
  /* ── Modal Clean ── */
  .modal-clean { border:none; border-radius:16px; box-shadow:0 20px 60px rgba(0,0,0,.14); overflow:hidden; }
  .modal-header-clean { display:flex; align-items:flex-start; gap:14px; padding:20px 24px 16px; border-bottom:1px solid #f0f4f8; }
  .modal-title-icon { width:40px; height:40px; border-radius:10px; display:flex; align-items:center; justify-content:center; font-size:16px; flex-shrink:0; }
  .modal-clean-title { font-size:14px; font-weight:700; color:#1a2b45; margin:0 0 2px; }
  .modal-clean-sub   { font-size:12px; color:#7a90b0; margin:0; }
  
  .field-group  { display:flex; flex-direction:column; gap:6px; padding:0 4px; }
  .field-label  { font-size:13px; font-weight:600; color:#1a2b45; }
  .field-input  { padding:9px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13.5px; color:#1a2b45; background:#fff; outline:none; width:100%; transition:border-color .15s; }
  .field-input:focus { border-color:#004AAD; box-shadow:0 0 0 3px rgba(0,74,173,.08); }
  
  /* ── Confirm Modal ── */
  .modal-icon-wrap { display:flex; justify-content:center; padding:28px 24px 0; }
  .modal-icon { width:60px; height:60px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:26px; }
  .modal-icon--danger { background:rgba(224,53,53,.1); color:#e03535; }
  .modal-body-clean { padding:16px 28px 8px; text-align:center; }
  .modal-heading    { font-size:17px; font-weight:700; color:#1a2b45; margin:0 0 8px; }
  .modal-subtext    { font-size:13.5px; color:#7a90b0; margin:0; line-height:1.6; }
  .modal-actions    { display:flex; gap:10px; padding:18px 24px 24px; justify-content:center; }
  .modal-btn        { display:inline-flex; align-items:center; justify-content:center; font-size:13px; font-weight:600; padding:9px 22px; border-radius:8px; border:none; cursor:pointer; flex:1; }
  .modal-btn--cancel  { background:#f4f7fb; color:#1a2b45; border:1px solid #dde5f0; } .modal-btn--cancel:hover { background:#e8eef6; }
  .modal-btn--confirm { background:#004AAD; color:#fff; } .modal-btn--confirm:hover { background:#003b8e; }
  .modal-btn--danger  { background:#e03535 !important; color:#fff; } .modal-btn--danger:hover { background:#c42b2b !important; }
  .me-2 { margin-right:6px; }
  
  /* ── Create modal helpers ── */
  .req        { color:#e03535; }
  .field-hint { font-size:11px; font-weight:400; color:#94a3b8; margin-left:4px; }
  .code-hint  { font-size:12px; color:#7a90b0; margin:0; display:flex; align-items:center; gap:4px; }

  /* ── Upload modal ── */
  .upload-template-row    { display:flex; align-items:center; gap:12px; background:#f8fafc; border:1px solid #e4eaf2; border-radius:9px; padding:12px 14px; }
  .upload-template-icon   { font-size:22px; color:#16a34a; flex-shrink:0; }
  .upload-template-title  { font-size:13px; font-weight:700; color:#1a2b45; margin:0 0 2px; }
  .upload-template-sub    { font-size:11.5px; color:#7a90b0; margin:0; }
  .btn-template-dl        { display:inline-flex; align-items:center; gap:5px; margin-left:auto; background:#004AAD; color:#fff; font-size:12px; font-weight:600; padding:6px 13px; border-radius:7px; border:none; cursor:pointer; white-space:nowrap; flex-shrink:0; }
  .btn-template-dl:hover  { background:#003b8e; }
  .upload-cols-hint       { display:flex; flex-wrap:wrap; gap:6px; }
  .col-chip               { background:#eff6ff; color:#004AAD; border:1px solid #bfdbfe; border-radius:5px; padding:2px 9px; font-size:11.5px; font-weight:600; font-family:monospace; }
  .col-chip--optional     { background:#f8fafc; color:#64748b; border-color:#e4eaf2; font-style:normal; }
  .col-chip--optional em  { font-style:italic; color:#94a3b8; }
  .upload-types-note      { font-size:11.5px; color:#7a90b0; margin:0; }
  .upload-types-note code { background:#f4f7fb; border-radius:4px; padding:1px 5px; font-size:11px; color:#1a2b45; }
  .upload-file-input      { padding:7px 10px; cursor:pointer; }
  .upload-result-box           { border-radius:8px; padding:12px 14px; }
  .upload-result-box--success  { background:#f0fdf4; border:1px solid #bbf7d0; }
  .upload-result-box--warn     { background:#fffbeb; border:1px solid #fde68a; }
  .upload-result-title         { font-size:13px; font-weight:600; color:#1a2b45; margin:0 0 6px; display:flex; align-items:center; }
  .upload-error-list           { margin:0; padding-left:18px; }
  .upload-error-list li        { font-size:12px; color:#92400e; margin-bottom:3px; }

  /* ── Responsive ── */
  @media(max-width:991px) { .page-header,.page-actions { flex-direction:column; align-items:stretch; } .search-input { width:100%; } }
  </style>